import { FC, useState, useRef, useEffect, useMemo } from 'react';
import { Card, ListGroup, ListGroupItem } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { customizeStore } from '@/stores';
import themeSettingStore from '@/stores/themeSetting';

const News: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'question' });
  const [currentPage, setCurrentPage] = useState(1);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { custom_side_news } = customizeStore((state) => state);
  const { theme, theme_config } = themeSettingStore((state) => state);
  const primaryColor = theme_config?.[theme]?.primary_color || '#0d6efd';

  // 解析 custom_side_news 格式：每行 "title, URL"
  const dataList = useMemo(() => {
    const newsText = custom_side_news as string;
    if (!newsText) return [];

    return newsText
      .split('\n')
      .map((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return null;

        // 按英文逗号分隔，只分割第一个逗号
        const firstCommaIndex = trimmedLine.indexOf(',');
        if (firstCommaIndex === -1) return null;

        const title = trimmedLine.substring(0, firstCommaIndex).trim();
        const url = trimmedLine.substring(firstCommaIndex + 1).trim();

        if (!title || !url) return null;

        return {
          id: `custom-${index}`,
          title,
          url,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [custom_side_news]);

  const totalPages = Math.min(Math.ceil(dataList.length / 6), 2);
  const startIndex = (currentPage - 1) * 6;
  const endIndex = startIndex + 6;
  const currentPageData = dataList.slice(startIndex, endIndex);

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startAutoPlay = () => {
    if (totalPages <= 1) return;
    stopAutoPlay();
    intervalRef.current = setInterval(() => {
      setCurrentPage((prev) => (prev === totalPages ? 1 : prev + 1));
    }, 5000);
  };

  const handlePageClick = (page: number) => {
    stopAutoPlay();
    setCurrentPage(page);
  };

  // 自动轮播
  useEffect(() => {
    if (totalPages > 1) {
      startAutoPlay();
    }
    return () => {
      stopAutoPlay();
    };
  }, [totalPages]);

  if (!dataList.length) {
    return null;
  }

  return (
    <Card style={{ marginTop: '16px' }}>
      <Card.Header className="text-nowrap text-capitalize">
        {t('news')}
      </Card.Header>
      <ListGroup variant="flush" style={{ height: '250px' }}>
        {currentPageData.map((li) => {
          return (
            <ListGroupItem key={li.id} as={Link} to={li.url} action>
              <div className="link-dark text-truncate-1">{li.title}</div>
            </ListGroupItem>
          );
        })}
      </ListGroup>
      {totalPages > 1 && (
        <Card.Footer className="d-flex justify-content-center gap-2">
          <div style={{ display: 'flex', gap: '10px' }}>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (page) => (
                <span
                  key={page}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor:
                      currentPage === page ? primaryColor : '#dee2e6',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => handlePageClick(page)}
                />
              ),
            )}
          </div>
        </Card.Footer>
      )}
    </Card>
  );
};
export default News;
